import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getBackendUrl } from "@/lib/backend";
import "./ManagementwiseBifurcationDashboard.css";
import { toTitleCase } from "../utils/toTitleCase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const formatIn = (value) => {
  if (value == null || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString("en-IN");
};

const formatPtr = (value) => {
  if (value == null || Number.isNaN(value)) return "—";
  return Number(value).toFixed(2);
};

const formatPct = (value) => {
  if (value == null || Number.isNaN(value)) return "—";
  return `${Number(value).toFixed(2)}%`;
};

const ManagementwiseBifurcationDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (scope.districtCode) params.district_code = scope.districtCode;
      if (scope.blockCode) params.block_code = scope.blockCode;
      if (scope.udiseCode) params.udise_code = scope.udiseCode;
      if (scope.districtName) params.district_name = scope.districtName;
      if (scope.blockName) params.block_name = scope.blockName;
      if (scope.schoolName) params.school_name = scope.schoolName;

      const res = await axios.get(`${API}/enrolment/management-bifurcation`, { params });
      setPayload(res.data || null);
    } catch (error) {
      console.error("Error fetching management bifurcation:", error);
      toast.error("Failed to load management bifurcation data");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
    scope.schoolName,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const tableRows = useMemo(() => {
    if (!payload?.groups?.length) return [];
    const rows = [];
    payload.groups.forEach((group) => {
      rows.push({
        key: `group-${group.group_id}`,
        rowType: "group",
        mgmtCode: "",
        management: group.group_label,
        schools: group.schools,
        enrolments: group.enrolments,
        teachers: group.teachers,
        ptr: group.ptr,
      });
      group.rows.forEach((detail) => {
        rows.push({
          key: `detail-${group.group_id}-${detail.mgmt_code}`,
          rowType: "detail",
          mgmtCode: detail.mgmt_code,
          management: detail.management,
          schools: detail.schools,
          enrolments: detail.enrolments,
          teachers: detail.teachers,
          ptr: detail.ptr,
        });
      });
    });
    if (payload.grand_total) {
      rows.push({
        key: "grand-total",
        rowType: "grand",
        mgmtCode: "",
        management: "Grand Total",
        schools: payload.grand_total.schools,
        enrolments: payload.grand_total.enrolments,
        teachers: payload.grand_total.teachers,
        ptr: payload.grand_total.ptr,
      });
    }
    return rows;
  }, [payload]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  const hasData = Boolean(payload?.groups?.length);

  return (
    <div className="mwb-page animate-fade-in" data-testid="managementwise-bifurcation-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Managementwise Bifurcation
          </h1>
          <p className="text-slate-500 mt-1">
            {payload?.scope_label || "Pune District"} • Schools, Enrolment, Teachers &amp; PTR by management type
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Management Data Available</h3>
            <p className="text-slate-500">Import Enrolment and Teacher data or adjust scope filters.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {payload.summary?.length > 0 && (
            <div className="mwb-summary-grid">
              {payload.summary.map((section) => (
                <div key={section.section} className="mwb-summary-card">
                  <h3>{toTitleCase(section.section)}</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Management</th>
                        <th>Number</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.map((row) => (
                        <tr key={`${section.section}-${row.bucket}`}>
                          <td>{row.bucket}</td>
                          <td style={{ textAlign: "right" }}>{formatIn(row.number)}</td>
                          <td>{formatPct(row.percentage)}</td>
                        </tr>
                      ))}
                      <tr className="mwb-summary-total">
                        <td>Grand Total</td>
                        <td style={{ textAlign: "right" }}>
                          {formatIn(
                            section.section === "School"
                              ? payload?.total_schools ?? section.grand_total
                              : section.grand_total
                          )}
                        </td>
                        <td>—</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}

          <div className="mwb-table-wrap">
            <table className="mwb-table">
              <thead>
                <tr>
                  <th>Mgt</th>
                  <th>Management</th>
                  <th>Schools</th>
                  <th>Enrolments</th>
                  <th>Teachers</th>
                  <th>PTR</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr
                    key={row.key}
                    className={
                      row.rowType === "group"
                        ? "mwb-group-row"
                        : row.rowType === "grand"
                          ? "mwb-grand-row"
                          : "mwb-detail-row"
                    }
                  >
                    <td>{row.mgmtCode || ""}</td>
                    <td>{row.management}</td>
                    <td>{formatIn(row.schools)}</td>
                    <td>{formatIn(row.enrolments)}</td>
                    <td>{formatIn(row.teachers)}</td>
                    <td>{formatPtr(row.ptr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ManagementwiseBifurcationDashboard;
